/**
 * Administração de pessoas, times e unidades (D12, §13.1 e §13.2).
 *
 * ── Três entidades num arquivo só ─────────────────────────────────────────
 *
 * Unidade, time e vínculo são separáveis no banco e inseparáveis no uso: não
 * existe tela de time sem a lista de unidades, nem de pessoa sem as duas. Um
 * arquivo por entidade espalharia o mesmo vocabulário — `status`, `carteira`,
 * o par unidade/time — por três lugares que precisam concordar.
 *
 * ── O que este contrato NÃO tem: exclusão ─────────────────────────────────
 *
 * Nenhuma das três se apaga. A decisão B2 separa desativar de excluir, e o
 * banco vai junto: a migração 030 revoga DELETE de `cliqcrm_app` em unidade e
 * time.
 *
 * Para pessoa vale o mesmo, e por um motivo a mais: a
 * [decisão de 24/08](../../../../docs/decisoes/2026-08-24-conversa-de-quem-sai.md)
 * diz que a conversa fica no nome de quem atendeu. Uma pessoa apagada levaria
 * esse nome embora — e o histórico passaria a não dizer quem falou com quem.
 */

import { z } from 'zod';
import { CarteiraSchema, IdSchema, PapelSchema } from './comum.js';
import { DinheiroInformadoSchema } from './organizacoes.js';
import { DinheiroDoPlanoSchema } from './planos.js';
import { InstanteSchema } from './valores.js';

/* ── Vocabulário comum às três ───────────────────────────────────────────── */

/**
 * Nome de unidade, time ou pessoa.
 *
 * O `trim` vem antes do mínimo de propósito: um nome de espaços passaria por
 * `min(2)` e apareceria em branco na lista, e o gestor não teria como
 * corrigi-lo sem adivinhar qual das linhas vazias é qual.
 */
const NomeSchema = z.string().trim().min(2).max(120);

/**
 * Situação de unidade e time.
 *
 * Duas palavras, não um booleano `ativo`. A §17.1 pede o vocabulário do
 * negócio, e `status: 'inativa'` sobrevive à chegada de um terceiro estado sem
 * virar `ativo: false, arquivado: true` — combinação que aceita o estado
 * impossível.
 */
export const SituacaoSchema = z.enum(['ativa', 'inativa']);

export type Situacao = z.infer<typeof SituacaoSchema>;

/* ── Unidade ─────────────────────────────────────────────────────────────── */

export const UnidadeSchema = z.object({
  id: IdSchema,
  nome: NomeSchema,
  status: SituacaoSchema,
  /** Quantas pessoas ativas estão vinculadas a ela. */
  pessoas: z.number().int().nonnegative(),
  criadaEm: InstanteSchema,
});

export type Unidade = z.infer<typeof UnidadeSchema>;

export const UnidadesSchema = z.object({ itens: z.array(UnidadeSchema) });

export type Unidades = z.infer<typeof UnidadesSchema>;

export const CriarUnidadeSchema = z.object({ nome: NomeSchema });

export type CriarUnidade = z.infer<typeof CriarUnidadeSchema>;

export const AlterarUnidadeSchema = z
  .object({ nome: NomeSchema.optional(), status: SituacaoSchema.optional() })
  .refine((c) => c.nome !== undefined || c.status !== undefined, {
    message: 'Informe ao menos `nome` ou `status`.',
  });

export type AlterarUnidade = z.infer<typeof AlterarUnidadeSchema>;

/* ── Time ────────────────────────────────────────────────────────────────── */

export const TimeSchema = z.object({
  id: IdSchema,
  nome: NomeSchema,
  /**
   * Time sem unidade é permitido: revenda de uma loja só não precisa inventar
   * uma hierarquia que ela não tem. É a mesma decisão da migração 030.
   */
  unidadeId: IdSchema.nullable(),
  unidadeNome: z.string().nullable(),
  status: SituacaoSchema,
  pessoas: z.number().int().nonnegative(),
  criadoEm: InstanteSchema,
});

export type Time = z.infer<typeof TimeSchema>;

export const TimesSchema = z.object({ itens: z.array(TimeSchema) });

export type Times = z.infer<typeof TimesSchema>;

export const CriarTimeSchema = z.object({
  nome: NomeSchema,
  unidadeId: IdSchema.nullish(),
});

export type CriarTime = z.infer<typeof CriarTimeSchema>;

export const AlterarTimeSchema = z
  .object({
    nome: NomeSchema.optional(),
    /**
     * `null` explícito tira o time da unidade; ausente não mexe. A distinção
     * importa porque `PATCH` com campo ausente significa "não mudei", e sem
     * ela não haveria como desvincular.
     */
    unidadeId: IdSchema.nullable().optional(),
    status: SituacaoSchema.optional(),
  })
  .refine((c) => Object.keys(c).length > 0, {
    message: 'Informe ao menos um campo para alterar.',
  });

export type AlterarTime = z.infer<typeof AlterarTimeSchema>;

/* ── Pessoa ──────────────────────────────────────────────────────────────── */

/**
 * Situação do vínculo, e ela tem um estado a mais que unidade e time.
 *
 * `provisionando` é exigência da D12: criar pessoa toca Keycloak, Twenty e
 * Chatwoot, e a §3.2 proíbe transação distribuída. Enquanto os três não
 * confirmam, a pessoa existe aqui e não existe lá — e o nome desse intervalo
 * precisa aparecer na interface, senão o gestor recarrega a tela procurando
 * alguém que "não foi criado".
 *
 * `falha_no_provisionamento` é o fim ruim: a saga não conseguiu nem completar
 * nem compensar. Exige mão humana, e por isso é estado nomeado em vez de a
 * pessoa sumir da lista.
 */
export const SituacaoDaPessoaSchema = z.enum([
  'provisionando',
  'ativo',
  'suspenso',
  'falha_no_provisionamento',
]);

export type SituacaoDaPessoa = z.infer<typeof SituacaoDaPessoaSchema>;

/**
 * O reflexo da pessoa no motor de CRM — e por que ele é um eixo SEPARADO do
 * `status`.
 *
 * `status` responde "esta pessoa faz parte da revenda?". Este campo responde
 * "o motor de CRM já sabe quem ela é?". São perguntas diferentes, e misturá-las
 * num campo só foi o que escondeu o problema até agora: a pessoa entrava como
 * `ativo` — porque, daqui, estava mesmo — e ninguém via que faltava metade.
 *
 * Ficava `pendente` porque o Twenty recusava `createWorkspaceMember` para
 * chave de API (medido contra a v2.26.1). No comercial próprio (070), não fica.
 * O convite VINCULA quando o membro já existe e deixa pendente quando não —
 * e deixar pendente é decisão, não falha.
 *
 * O efeito de ficar pendente é preciso: a pessoa não recebe conversa por
 * transferência e não figura como responsável por oportunidade, porque as duas
 * coisas atravessam `crm_member_source_id`. Quem convida precisa enxergar isso
 * na lista, e não descobrir dias depois pela boca de um vendedor que tentou
 * transferir.
 */
export const VinculoNoCrmSchema = z.enum(['ligado', 'pendente']);

export type VinculoNoCrm = z.infer<typeof VinculoNoCrmSchema>;

/**
 * O reflexo da pessoa no motor de CONVERSA — o mesmo problema do CRM, com uma
 * diferença que muda o desenho.
 *
 * O agente do Chatwoot nasce no convite, e só ali. Duas portas deixam a pessoa
 * `ativo` sem ele, e as duas são "o mundo mudou depois":
 *
 *   · convidada ANTES de a revenda ligar o Chatwoot — o passo conclui sem
 *     criar nada, de propósito, porque exigir a conexão para admitir alguém
 *     impediria de montar a equipe antes da integração ficar pronta;
 *   · promovida para um papel que ganha agente — `operacao_cliqfy` e `sistema`
 *     não viram agente por decisão da §13.2, e o eco de papel só troca o papel
 *     de um agente que já existe, nunca cria.
 *
 * ── Por que quatro estados, e não dois ────────────────────────────────────
 *
 * Porque não há coluna guardando o id do agente, e a resposta vem de perguntar
 * ao motor. Isso é escolha: um id gravado ficaria velho no dia em que alguém
 * apagasse o agente pelo Chatwoot, e a tela mostraria verde sobre um reflexo
 * quebrado — mentira que só aparece quando o vendedor tenta transferir.
 *
 * O preço de perguntar é não ter resposta às vezes, e `desconhecido` é isso
 * dito em voz alta. Ele cobre também a revenda sem Chatwoot ligado: ali
 * ninguém tem agente, e marcar todo mundo como `pendente` mandaria consertar
 * trinta pessoas quando o que falta é uma conexão.
 *
 * `nao_se_aplica` existe pelo motivo oposto: para `operacao_cliqfy` não falta
 * nada. Chamar isso de `pendente` mandaria criar um agente que a §13.2 proíbe.
 */
export const AcessoAoAtendimentoSchema = z.enum([
  'ligado',
  'pendente',
  'nao_se_aplica',
  'desconhecido',
]);

export type AcessoAoAtendimento = z.infer<typeof AcessoAoAtendimentoSchema>;

export const PessoaSchema = z.object({
  /** O id do VÍNCULO (`core.memberships`), não o da pessoa. Ver nota abaixo. */
  id: IdSchema,
  /**
   * O id da pessoa (`core.users`), que é global e pode aparecer em mais de uma
   * revenda. Vem separado do `id` porque as duas perguntas são diferentes:
   * "qual vínculo eu edito" e "quem é esta pessoa".
   */
  usuarioId: IdSchema,
  nome: z.string(),
  email: z.string().email(),
  papel: PapelSchema,
  carteira: CarteiraSchema,
  unidadeId: IdSchema.nullable(),
  unidadeNome: z.string().nullable(),
  timeId: IdSchema.nullable(),
  timeNome: z.string().nullable(),
  status: SituacaoDaPessoaSchema,
  /** Ver `VinculoNoCrmSchema`: é outro eixo, e não um degrau do `status`. */
  vinculoNoCrm: VinculoNoCrmSchema,
  /** Ver `AcessoAoAtendimentoSchema`. Vem de perguntar ao motor, não de coluna. */
  acessoAoAtendimento: AcessoAoAtendimentoSchema,
  /** A marca "acesso ao prontuário" (ADR-0022). Ausente é falso. */
  acessoAoProntuario: z.boolean().optional(),
  /**
   * A pessoa é Super Admin da Cliqfy: a organização não desativa o acesso dela,
   * e quem tira o papel é a Cliqfy (decisão de 30/10/2026, emenda ao ADR-0025).
   * Ausente é falso.
   */
  superAdmin: z.boolean().optional(),
  criadoEm: InstanteSchema,
  /**
   * O link assinado da foto de perfil (061), ou nulo — e a tela mostra as
   * iniciais. Vale cinco minutos, como todo link de foto.
   */
  fotoUrl: z.string().nullable(),
});

export type Pessoa = z.infer<typeof PessoaSchema>;

/**
 * Como a pessoa recém-convidada vai entrar pela primeira vez.
 *
 * Sai SÓ na resposta do convite, e nunca numa listagem: a senha provisória tem
 * de existir por uma sessão, na tela de quem acabou de cadastrar, e não virar
 * um campo que qualquer consulta devolve depois.
 */
export const PrimeiroAcessoSchema = z.discriminatedUnion('via', [
  z.object({
    via: z.literal('email'),
    /** Para onde foi o link de definir senha. */
    enviadoPara: z.string().email(),
  }),
  z.object({
    via: z.literal('senha_provisoria'),
    /**
     * Mostre UMA vez e não guarde.
     *
     * Ela existe porque este ambiente não tem remetente de e-mail. É temporária
     * no provedor de identidade: a pessoa é obrigada a trocá-la no primeiro
     * acesso, então a exposição dura uma sessão.
     */
    senha: z.string(),
  }),
  /** A pessoa já tinha conta — de outra revenda. A senha dela continua sendo a dela. */
  z.object({ via: z.literal('ja_tinha_conta') }),
]);

export type PrimeiroAcesso = z.infer<typeof PrimeiroAcessoSchema>;

export const PessoaConvidadaSchema = PessoaSchema.extend({
  primeiroAcesso: PrimeiroAcessoSchema,
});

export type PessoaConvidada = z.infer<typeof PessoaConvidadaSchema>;

export const PessoasSchema = z.object({ itens: z.array(PessoaSchema) });

export type Pessoas = z.infer<typeof PessoasSchema>;

/**
 * O par unidade/time, que aparece em criar e em alterar.
 *
 * A coerência entre os dois — time que pertence a outra unidade — é regra de
 * aplicação, e não dá para expressar em zod: depende do banco. A migração 030
 * registra isso no próprio schema, para quem for procurar a garantia lá.
 */
const LotacaoSchema = {
  unidadeId: IdSchema.nullish(),
  timeId: IdSchema.nullish(),
};

export const ConvidarPessoaSchema = z.object({
  nome: NomeSchema,
  /**
   * O e-mail é a identidade: é por ele que o Keycloak reconhece quem já existe,
   * e é para ele que o convite vai. Minúsculas porque `Ana@` e `ana@` são a
   * mesma caixa, e tratá-los como duas pessoas criaria a segunda conta.
   */
  email: z.string().trim().toLowerCase().email(),
  papel: PapelSchema,
  carteira: CarteiraSchema,
  /**
   * "Sim, sei que este e-mail já tem conta em outra revenda, e quero que ela
   * entre também nesta."
   *
   * Sem isto, o convite para um e-mail conhecido em outra revenda é recusado
   * com `CONFIRMACAO_EXIGIDA`. Desde o realm compartilhado, confirmar NÃO cria
   * uma segunda conta: a mesma conta, com a mesma senha, ganha um vínculo com
   * esta revenda, e a pessoa escolhe a revenda ao entrar.
   *
   * O nome ficou do tempo em que confirmar criava a segunda conta. Trocá-lo
   * quebraria o `/v1` por causa de um nome, e o efeito está descrito aqui.
   */
  confirmarSegundaConta: z.boolean().optional(),
  /**
   * "Sim, contrato 1 usuário adicional por este valor, que vi na tela" (#768).
   *
   * Com a organização no limite de usuários contratados, o convite sem isto é
   * recusado com `CONFIRMACAO_EXIGIDA`, e a extensão `limite` traz o valor
   * deste cliente. Com ele, o contrato sobe 1 junto com o convite, se o valor
   * ainda for o de hoje; se mudou, a recusa volta com o valor novo. Só o
   * administrador da organização contrata.
   */
  contratarUsuarioAdicional: DinheiroInformadoSchema.optional(),
  ...LotacaoSchema,
});

export type ConvidarPessoa = z.infer<typeof ConvidarPessoaSchema>;

export const AlterarPessoaSchema = z
  .object({
    papel: PapelSchema.optional(),
    carteira: CarteiraSchema.optional(),
    unidadeId: IdSchema.nullable().optional(),
    timeId: IdSchema.nullable().optional(),
  })
  .refine((c) => Object.keys(c).length > 0, {
    message: 'Informe ao menos um campo para alterar.',
  });

export type AlterarPessoa = z.infer<typeof AlterarPessoaSchema>;

/**
 * Desativar e reativar.
 *
 * A justificativa é obrigatória na desativação pela mesma razão da D13: tirar
 * o acesso de alguém é a primeira coisa que se pergunta num incidente, e
 * "quem" e "quando" o `audit_logs` já responde sozinho. O "por quê" é o único
 * que ninguém reconstrói depois.
 */
export const DesativarPessoaSchema = z.object({
  justificativa: z.string().trim().min(10).max(500),
});

export type DesativarPessoa = z.infer<typeof DesativarPessoaSchema>;

/**
 * Reativar quem foi desativado. O corpo só traz a confirmação de contratar 1
 * usuário adicional, quando a organização está no limite (#768), como no
 * convite. Ele pode faltar, e o campo que ele não conhece é ignorado, como
 * antes de o corpo existir: sem a confirmação, reativa como sempre, se houver
 * vaga.
 */
export const ReativarPessoaSchema = z.object({
  /** O mesmo do convite (`ConvidarPessoaSchema.contratarUsuarioAdicional`). */
  contratarUsuarioAdicional: DinheiroInformadoSchema.optional(),
});

export type ReativarPessoa = z.infer<typeof ReativarPessoaSchema>;

/**
 * Mais um bloco de armazenamento (#718), pela tela de Pessoas. `valor_aceito`
 * é o valor do bloco que a pessoa viu (`valor_do_bloco` dos limites): o
 * contrato sobe na hora se ele ainda for o de hoje; sem ele, ou se mudou, a
 * recusa volta com o valor de hoje. Só o administrador da organização contrata.
 */
export const ContratarArmazenamentoSchema = z
  .object({
    valor_aceito: DinheiroInformadoSchema.optional(),
  })
  .strict();

export type ContratarArmazenamento = z.infer<typeof ContratarArmazenamentoSchema>;

/**
 * Os limites do contrato da organização, para a tela de Pessoas (#768). Os
 * usuários: os contratados, os em uso (vínculos ativos e convites pendentes,
 * fora as contas técnicas) e o valor do usuário adicional deste cliente. E o
 * armazenamento (#716, #717): o contratado e o em uso, que a faixa da tela
 * avisa a partir de 90%. Sem contrato, os dois são nulos, e não há limite.
 */
export const LimitesDaOrganizacaoSchema = z
  .object({
    usuarios: z
      .object({
        contratados: z.number().int(),
        em_uso: z.number().int(),
        /** Nulo quando o contrato não tem o valor do usuário adicional. */
        usuario_adicional: DinheiroDoPlanoSchema.nullable(),
        /** Quem chama contrata 1 usuário adicional pelo convite ou pela reativação. */
        pode_contratar: z.boolean(),
      })
      .strict()
      .nullable(),
    armazenamento: z
      .object({
        contratado_gb: z.number().int(),
        /**
         * O que conta na cota: os documentos pelo R2 e os anexos das conversas
         * pelo banco, em bytes (1 GB = `BYTES_POR_GB`); os anexos de uma conta
         * do Atendimento dividida ficam de fora. Nulo enquanto a organização
         * não foi medida.
         */
        em_uso_bytes: z.number().int().nullable(),
        /** A medição mais antiga entre os tipos de arquivo; nula enquanto não foi medida. */
        medido_em: InstanteSchema.nullable(),
        /** Quantos GB um bloco contratado pela tela soma ao contrato (#718). */
        bloco_gb: z.number().int(),
        /**
         * O valor do bloco para este cliente, por mês: o GB adicional do
         * contrato vezes `bloco_gb`. Nulo quando o contrato não tem o valor do
         * GB adicional, e aí quem resolve é a equipe da Cliqfy.
         */
        valor_do_bloco: DinheiroDoPlanoSchema.nullable(),
        /** Quem chama contrata o bloco por aqui: o administrador da organização. */
        pode_contratar: z.boolean(),
      })
      .strict()
      .nullable(),
  })
  .strict();

export type LimitesDaOrganizacao = z.infer<typeof LimitesDaOrganizacaoSchema>;

/**
 * O que a desativação fez, engine por engine.
 *
 * Não é `{ ok: true }`. A Armadilha da D12 é exatamente a desativação que
 * parece ter funcionado — se o Keycloak recusou, a pessoa continua entrando, e
 * uma resposta de sucesso faria a interface mentir sobre isso.
 */
export const DesativacaoSchema = z.object({
  id: IdSchema,
  status: SituacaoDaPessoaSchema,
  /**
   * Uma linha por sistema tocado, com o que aconteceu em cada um. Falha aqui
   * é incidente aberto, e o campo `alerta` diz se ele foi.
   */
  engines: z.array(
    z.object({
      engine: z.enum(['keycloak', 'chatwoot', 'crm']),
      resultado: z.enum(['aplicado', 'dispensado', 'falhou']),
      detalhe: z.string(),
    }),
  ),
  alerta: z.boolean(),
});

export type Desativacao = z.infer<typeof DesativacaoSchema>;

/* ── Redefinição de senha (#1167) ────────────────────────────────────────── */

/**
 * Redefinir a senha de alguém da equipe.
 *
 * A justificativa é obrigatória pelo motivo do reset de segundo fator (B15):
 * quem troca a senha de outra pessoa consegue entrar como ela, e o "por quê" é
 * o que a trilha não reconstrói depois.
 */
export const RedefinirSenhaSchema = z.object({
  justificativa: z.string().trim().min(10).max(500),
});

export type RedefinirSenha = z.infer<typeof RedefinirSenhaSchema>;

/**
 * Como a pessoa entra de novo: as duas formas do primeiro acesso do convite.
 *
 * Com `senha_provisoria`, mostre UMA vez e não guarde, como no convite. Ela é
 * temporária no provedor: a pessoa é obrigada a trocá-la no próximo acesso.
 *
 * `sessoesEncerradas` é `false` quando o login não confirmou o encerramento:
 * a senha foi trocada, mas quem já estava conectado continua até sair. A tela
 * diz isso, em vez de afirmar que todos saíram.
 */
export const RedefinicaoDeSenhaSchema = z.discriminatedUnion('via', [
  z.object({
    via: z.literal('email'),
    /** Para onde foi o link de definir senha. */
    enviadoPara: z.string().email(),
    sessoesEncerradas: z.boolean(),
  }),
  z.object({
    via: z.literal('senha_provisoria'),
    senha: z.string(),
    sessoesEncerradas: z.boolean(),
  }),
]);

export type RedefinicaoDeSenha = z.infer<typeof RedefinicaoDeSenhaSchema>;

/* ── Reprovisionamento ───────────────────────────────────────────────────── */

/**
 * Completar nas engines quem já entrou — a segunda chance do convite.
 *
 * ── Por que uma segunda chance precisa existir ────────────────────────────
 *
 * O convite toca as engines UMA vez, no instante em que a pessoa é admitida, e
 * o que não deu certo ali não dá certo nunca mais. Só que os dois casos que
 * ficam para trás são os NORMAIS, não exceções:
 *
 *   · o Twenty não deixa criar membro por chave de API, então o passo dele
 *     apenas vincula — e vincula ao que já existe naquele segundo;
 *   · o Chatwoot só é tocado se a revenda já tiver ligado a integração, o que
 *     costuma acontecer depois de a equipe estar montada.
 *
 * Nos dois, a saída é humana e acontece mais tarde. Sem esta operação, ninguém
 * percebia: a pessoa ficava `ativo` — porque daqui estava mesmo — e quem
 * descobria a metade que faltava era o vendedor, dias depois, levando recusa ao
 * tentar passar uma conversa.
 *
 * ── Um comando só, e não um por engine ────────────────────────────────────
 *
 * Do lado de quem administra a pergunta é uma: "esta pessoa está incompleta,
 * completa aí". Dois botões obrigariam a diagnosticar qual motor falta antes de
 * saber qual apertar — e o diagnóstico é justamente o que a pessoa não tem.
 *
 * ── E NÃO é saga, ao contrário do convite ─────────────────────────────────
 *
 * Aqui sucesso parcial é bom. Criar o agente do atendimento enquanto o cadastro
 * do CRM segue pendente é progresso de verdade, e compensar apagaria o agente
 * recém-criado para "manter a consistência" de um estado que ninguém queria.
 *
 * A saga existe para proteger um tudo-ou-nada, e aqui não há: a pessoa já está
 * no sistema, e cada reflexo vale por si. Por isso a resposta é a mesma da
 * desativação — o que aconteceu em CADA motor, com `alerta` quando algum
 * recusou.
 */
export const ReprovisionamentoSchema = z.object({
  pessoa: PessoaSchema,
  /**
   * Uma linha por motor tocado. `dispensado` não é falha: é o motor que já
   * estava certo, o papel que não recebe agente, ou a integração que a revenda
   * ainda não ligou.
   */
  engines: z.array(
    z.object({
      engine: z.enum(['chatwoot', 'crm']),
      resultado: z.enum(['aplicado', 'dispensado', 'falhou']),
      detalhe: z.string(),
    }),
  ),
  /** `true` quando algum motor recusou. O que deu certo continua valendo. */
  alerta: z.boolean(),
});

export type Reprovisionamento = z.infer<typeof ReprovisionamentoSchema>;

/* ── Redistribuição de carteira ──────────────────────────────────────────── */

/**
 * Passar a carteira de uma pessoa para outra, em lote.
 *
 * ── Por que isto não acontece sozinho na desativação ──────────────────────
 *
 * Porque o dono do produto decidiu que não deve:
 *
 * > *"desativar a pessoa deve deixar as conversas inativadas ainda no nome da
 * > pessoa … e só troca o dono quando tiver novamente uma interação"*
 *
 * Conversa segue essa regra e não entra aqui. O que entra é **negócio em
 * aberto**: oportunidade sem dono não avança, e ninguém liga para um cliente
 * que está na lista de um vendedor que saiu.
 *
 * ── Em lote, e por isso mesmo com confirmação do total ────────────────────
 *
 * `esperado` é o número que o gestor viu na tela. Se o banco discordar, a
 * operação não roda — entre a conferência e o clique alguém pode ter movido
 * mais dez, e transferir "tudo que houver" é como o gestor descobre que moveu
 * quarenta em vez de trinta.
 */
/**
 * O tamanho da carteira de uma pessoa, e o que impede movê-la.
 *
 * ── O impedimento vem JUNTO, e não como erro ──────────────────────────────
 *
 * Quem pergunta está montando a operação, não executando: a tela abre o
 * diálogo, mostra o número e só então pede a justificativa. Um 409 aqui faria
 * a tela traduzir um erro para uma frase de formulário — e com o impedimento à
 * vista a pessoa não digita dez linhas para descobrir no fim que a origem nem
 * tem vínculo com o CRM.
 */
export const CarteiraDaPessoaSchema = z.object({
  /** Só os que a revenda declarou como etapas ABERTAS (C17). */
  negocios_em_aberto: z.number().int().nonnegative(),
  /** `null` quando dá para mover. A frase do obstáculo quando não. */
  impedimento: z.string().nullable(),
});

export type CarteiraDaPessoa = z.infer<typeof CarteiraDaPessoaSchema>;

export const RedistribuirSchema = z.object({
  deUsuarioId: IdSchema,
  paraUsuarioId: IdSchema,
  esperado: z.number().int().nonnegative(),
  justificativa: z.string().trim().min(10).max(500),
});

export type Redistribuir = z.infer<typeof RedistribuirSchema>;

export const RedistribuicaoSchema = z.object({
  transferidas: z.number().int().nonnegative(),
  /**
   * O que NÃO foi transferido, e por quê. Silenciar isto faria "30 movidas"
   * significar coisas diferentes conforme o dia.
   */
  avisos: z.array(z.string()),
});

export type Redistribuicao = z.infer<typeof RedistribuicaoSchema>;
